import { useEffect, useState } from 'react';
import { toMajor } from '../../contracts/money';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle2,
  CreditCard,
  Database,
  HandCoins,
  Tag,
  X,
} from 'lucide-react';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { Money } from '../../components/Money';
import { OptionCard } from '../../components/OptionCard';
import { PageHeader } from '../../components/PageHeader';
import { cn } from '../../lib/cn';
import { startCheckout } from '../../api/billing';
import { useActiveBusiness, useSubscription } from '../../api/hooks';
import { usePackages, usePricesByPackage } from '../../api/catalog';

const limitDesc = (loc: number | 'unlimited', usr: number | 'unlimited') =>
  `${loc === 'unlimited' ? 'Unlimited' : loc} location${loc === 1 ? '' : 's'} · ${usr === 'unlimited' ? 'Unlimited' : usr} users`;

/**
 * Change plan — §5.12
 * Full page version: plan picker top, proration card middle, coupon + payment + confirm at bottom.
 */
export function ChangePlan() {
  const navigate = useNavigate();
  const [migrate, setMigrate] = useState(false);
  const [effectiveDate, setEffectiveDate] = useState<'now' | 'next'>('now');

  const [coupon, setCoupon] = useState('EID2026');
  const [couponApplied, setCouponApplied] = useState<string | null>('EID2026');
  const [couponError, setCouponError] = useState<string | null>(null);

  const [pay, setPay] = useState<'sslcommerz' | 'new' | 'cash'>('sslcommerz');
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [payError, setPayError] = useState<string | null>(null);

  const { data: currentBusiness } = useActiveBusiness();
  const { data: prices = [] } = usePricesByPackage();
  const { data: packages = [] } = usePackages();
  const { data: subscription } = useSubscription(currentBusiness?.id ?? '');

  // Plan catalog from the API (published + public packages; monthly price).
  const sellable = packages.filter((p) => p.status === 'published' && p.visibility === 'public');
  const monthlyMinor = (pkgId: string) =>
    prices.find((pr) => pr.packageId === pkgId && pr.interval === 'month')?.amount ?? 0;

  // Current plan + target come from the live subscription / catalog (no hardcoded plans).
  // Match the live subscription's plan name to a catalog package (the API subscription
  // shape carries plan name + renewsAt, not a packageId — contract migration pending).
  const currentPkgId = sellable.find((p) => p.name === subscription?.plan)?.id ?? sellable[0]?.id ?? '';
  const [target, setTarget] = useState('');
  useEffect(() => {
    if (target) return;
    const firstOther = sellable.find((p) => p.id !== currentPkgId) ?? sellable[0];
    if (firstOther) setTarget(firstOther.id);
  }, [sellable, currentPkgId, target]);

  const currentPkg = sellable.find((p) => p.id === currentPkgId);
  const targetPkg = sellable.find((p) => p.id === target);
  const currentName = subscription?.plan ?? currentPkg?.name ?? '—';
  const targetName = targetPkg?.name ?? '—';
  const currentMinor = monthlyMinor(currentPkgId);
  const targetMinor = monthlyMinor(target);
  const targetPrice = prices.find((p) => p.packageId === target && p.interval === 'month' && p.currency === 'BDT');

  // Proration period from the live subscription (fallback: 30-day month, ~12 days left).
  const PERIOD_DAYS = subscription?.interval === 'year' ? 365 : 30;
  const daysRemaining = (() => {
    const end = subscription?.renewsAt ? new Date(subscription.renewsAt).getTime() : 0;
    if (!end) return Math.min(PERIOD_DAYS, 12);
    return Math.max(0, Math.min(PERIOD_DAYS, Math.ceil((end - Date.now()) / 86400000)));
  })();

  const targetCharge = Math.round((targetMinor * daysRemaining) / PERIOD_DAYS);
  const currentCredit = Math.round((currentMinor * daysRemaining) / PERIOD_DAYS);
  const subtotalToday = targetCharge - currentCredit;
  const couponDiscount = couponApplied ? Math.round(subtotalToday * 0.2) : 0;
  const netToday = effectiveDate === 'now' ? Math.max(0, subtotalToday - couponDiscount) : 0;

  const nextRenewalCoupon = couponApplied ? Math.round(targetMinor * 0.2) : 0;
  const nextRenewal = targetMinor - nextRenewalCoupon;

  // The selected target's tier unlocks dedicated-DB migration (Regular+ / multi-location).
  const tLoc = targetPkg?.limits.locations;
  const targetAllowsDedicatedDb = tLoc === 'unlimited' || (typeof tLoc === 'number' && tLoc >= 3);

  function applyCoupon() {
    setCouponError(null);
    if (!coupon.trim()) return;
    if (coupon.trim().toUpperCase() === 'EXPIRED-2025') {
      setCouponError('This code has expired.');
      setCouponApplied(null);
      return;
    }
    setCouponApplied(coupon.trim().toUpperCase());
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!confirmed) return;
    setPayError(null);
    setSubmitting(true);
    // Cash is recorded by an admin later — nothing to charge now.
    if (pay === 'cash') {
      setTimeout(() => navigate({ to: '/subscription' }), 1100);
      return;
    }
    // Online: start an SSLCommerz checkout and hand off to the hosted page.
    if (currentBusiness && targetPrice) {
      try {
        const session = await startCheckout({ businessId: currentBusiness.id, priceId: targetPrice.id });
        window.location.href = session.gatewayPageURL;
        return;
      } catch {
        setPayError('Could not start the payment. Please try again.');
        setSubmitting(false);
        return;
      }
    }
    setPayError('This plan is not available for online payment right now.');
    setSubmitting(false);
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-3xl mx-auto">
      <div className="mb-3">
        <Link
          to="/subscription"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200"
        >
          <ArrowLeft className="w-4 h-4" /> Back to subscription
        </Link>
      </div>

      <PageHeader
        eyebrow="Subscription"
        title="Change plan"
        subtitle={
          <span>
            Currently <strong className="text-zinc-800 dark:text-zinc-100">{currentName}</strong>{' '}
            · ৳{toMajor(currentMinor)}/mo · {daysRemaining} days remaining
          </span>
        }
      />

      <form onSubmit={onSubmit} className="space-y-5">
        {/* Plan picker */}
        <Card className="p-5 sm:p-6">
          <h2 className="text-base font-semibold text-zinc-950 dark:text-zinc-50 mb-3">
            Pick a new plan
          </h2>
          <div className="space-y-3">
            {sellable.map((p) => (
              <OptionCard
                key={p.id}
                selected={target === p.id}
                onSelect={() => setTarget(p.id)}
              >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-zinc-950 dark:text-zinc-50">
                        {p.name}
                      </span>
                      {p.id === currentPkgId && (
                        <span className="text-xs text-zinc-500 dark:text-zinc-400">
                          (current plan)
                        </span>
                      )}
                      {p.marketingBadge && (
                        <span className="px-1.5 py-px rounded text-[10px] font-medium bg-brand text-white dark:bg-brand-dm dark:text-zinc-950">
                          {p.marketingBadge}
                        </span>
                      )}
                    </div>
                    <p className="mt-1 text-xs text-zinc-600 dark:text-zinc-300">
                      {limitDesc(p.limits.locations, p.limits.users)}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <Money
                      minor={monthlyMinor(p.id)}
                      hideCents
                      tone="neutral"
                      className="font-semibold text-zinc-950 dark:text-zinc-50"
                    />
                    <div className="text-xs text-zinc-500 dark:text-zinc-400">/ month</div>
                  </div>
                </div>
              </OptionCard>
            ))}
          </div>

          {targetAllowsDedicatedDb && (
            <label className="mt-4 flex items-start gap-3 p-3 rounded-md border border-zinc-200 dark:border-zinc-600 cursor-pointer">
              <input
                type="checkbox"
                checked={migrate}
                onChange={(e) => setMigrate(e.target.checked)}
                className="mt-0.5 rounded border-zinc-300 dark:border-zinc-600 text-brand focus:ring-brand"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2 text-sm font-medium text-zinc-950 dark:text-zinc-50">
                  <Database className="w-4 h-4 text-brand dark:text-brand-dm" />
                  Also migrate to a dedicated database
                </div>
                <p className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-400">
                  Background migration · ~5 min · 30-day rollback window
                </p>
              </div>
            </label>
          )}

          <div className="mt-4 pt-4 border-t border-zinc-200 dark:border-zinc-700">
            <span className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">
              Effective date
            </span>
            <div className="grid sm:grid-cols-2 gap-3">
              <OptionCard
                selected={effectiveDate === 'now'}
                onSelect={() => setEffectiveDate('now')}
              >
                <div className="font-medium text-zinc-950 dark:text-zinc-50 text-sm">
                  Immediately
                </div>
                <p className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-400">
                  Prorated for the remaining {daysRemaining} days
                </p>
              </OptionCard>
              <OptionCard
                selected={effectiveDate === 'next'}
                onSelect={() => setEffectiveDate('next')}
              >
                <div className="font-medium text-zinc-950 dark:text-zinc-50 text-sm">
                  At next renewal
                </div>
                <p className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-400">
                  Switches on 03 Jun 2026
                </p>
              </OptionCard>
            </div>
          </div>
        </Card>

        {/* Coupon */}
        <Card className="p-5 sm:p-6">
          <h2 className="text-base font-semibold text-zinc-950 dark:text-zinc-50 mb-3">
            Apply a coupon
          </h2>
          <div className="flex items-stretch gap-2">
            <input
              type="text"
              value={coupon}
              onChange={(e) => setCoupon(e.target.value.toUpperCase())}
              placeholder="EID2026"
              className={cn(
                'flex-1 px-3 py-2.5 text-sm rounded-md mono uppercase',
                'bg-white dark:bg-zinc-900 text-zinc-950 dark:text-zinc-50',
                'border border-zinc-300 dark:border-zinc-700',
                'focus:outline-none focus:ring-2 focus:ring-brand dark:focus:ring-brand-dm',
              )}
            />
            <Button
              variant="secondary"
              type="button"
              onClick={applyCoupon}
              iconLeft={<Tag className="w-4 h-4" />}
            >
              Apply
            </Button>
          </div>
          {couponApplied && (
            <p className="mt-2 text-xs flex items-center gap-1.5 text-emerald-700 dark:text-emerald-400">
              <Check className="w-3.5 h-3.5" />
              <span className="font-medium">{couponApplied}</span> applied — 20% off for 3
              months
            </p>
          )}
          {couponError && (
            <p className="mt-2 text-xs text-rose-600 dark:text-rose-400 flex items-center gap-1.5">
              <X className="w-3.5 h-3.5" /> {couponError}
            </p>
          )}
        </Card>

        {/* Proration math */}
        {effectiveDate === 'now' && target !== currentPkgId && (
          <Card className="p-5 sm:p-6">
            <h2 className="text-base font-semibold text-zinc-950 dark:text-zinc-50 mb-4">
              Proration math
            </h2>

            <div className="rounded-lg border border-zinc-200 dark:border-zinc-600 overflow-hidden">
              <div className="px-4 py-3 bg-zinc-50 dark:bg-zinc-800/40 border-b border-zinc-200 dark:border-zinc-700">
                <div className="text-xs uppercase tracking-wider font-medium text-zinc-500 dark:text-zinc-400">
                  Today's charge
                </div>
              </div>
              <dl className="px-4 py-3 text-sm space-y-2.5">
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-zinc-700 dark:text-zinc-200">
                    {targetName} ({daysRemaining} days remaining)
                  </dt>
                  <dd className="tabular-nums text-zinc-700 dark:text-zinc-200">
                    ৳{toMajor(targetMinor)} × {daysRemaining}/{PERIOD_DAYS} ={' '}
                    <span className="font-medium text-zinc-950 dark:text-zinc-50">
                      <Money minor={targetCharge} hideCents tone="neutral" />
                    </span>
                  </dd>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-zinc-700 dark:text-zinc-200">
                    Credit for unused {currentName}
                  </dt>
                  <dd className="tabular-nums text-emerald-700 dark:text-emerald-400">
                    − <Money minor={currentCredit} hideCents tone="neutral" />
                  </dd>
                </div>
                {couponApplied && (
                  <div className="flex items-center justify-between gap-3">
                    <dt className="text-zinc-700 dark:text-zinc-200">
                      Coupon {couponApplied} (20% off)
                    </dt>
                    <dd className="tabular-nums text-emerald-700 dark:text-emerald-400">
                      − <Money minor={couponDiscount} hideCents tone="neutral" />
                    </dd>
                  </div>
                )}
                <div className="flex items-center justify-between border-t border-zinc-200 dark:border-zinc-700 pt-2.5 mt-2.5">
                  <dt className="font-semibold text-zinc-950 dark:text-zinc-50">
                    Net today
                  </dt>
                  <dd className="tabular-nums font-semibold text-lg text-zinc-950 dark:text-zinc-50">
                    <Money minor={netToday} hideCents tone="neutral" />
                  </dd>
                </div>
              </dl>
            </div>

            <div className="mt-4 rounded-lg border border-zinc-200 dark:border-zinc-600 overflow-hidden">
              <div className="px-4 py-3 bg-zinc-50 dark:bg-zinc-800/40 border-b border-zinc-200 dark:border-zinc-700">
                <div className="text-xs uppercase tracking-wider font-medium text-zinc-500 dark:text-zinc-400">
                  Next charge on 03 Jun 2026
                </div>
              </div>
              <dl className="px-4 py-3 text-sm space-y-2.5">
                <div className="flex items-center justify-between">
                  <dt className="text-zinc-700 dark:text-zinc-200">
                    {targetName} (full month)
                  </dt>
                  <dd className="tabular-nums text-zinc-700 dark:text-zinc-200">
                    <Money minor={targetMinor} hideCents tone="neutral" />
                  </dd>
                </div>
                {couponApplied && (
                  <div className="flex items-center justify-between">
                    <dt className="text-zinc-700 dark:text-zinc-200">
                      Coupon {couponApplied} (month 2 of 3)
                    </dt>
                    <dd className="tabular-nums text-emerald-700 dark:text-emerald-400">
                      − <Money minor={nextRenewalCoupon} hideCents tone="neutral" />
                    </dd>
                  </div>
                )}
                <div className="flex items-center justify-between border-t border-zinc-200 dark:border-zinc-700 pt-2.5 mt-2.5">
                  <dt className="font-semibold text-zinc-950 dark:text-zinc-50">Next month</dt>
                  <dd className="tabular-nums font-semibold text-zinc-950 dark:text-zinc-50">
                    <Money minor={nextRenewal} hideCents tone="neutral" />
                  </dd>
                </div>
              </dl>
            </div>
          </Card>
        )}

        {/* Pay with */}
        <Card className="p-5 sm:p-6">
          <h2 className="text-base font-semibold text-zinc-950 dark:text-zinc-50 mb-3">
            Pay with
          </h2>
          <div className="space-y-3">
            <OptionCard
              selected={pay === 'sslcommerz'}
              onSelect={() => setPay('sslcommerz')}
            >
              <div className="flex items-start gap-3">
                <CreditCard className="w-5 h-5 mt-0.5 text-brand dark:text-brand-dm flex-shrink-0" />
                <div>
                  <div className="font-medium text-zinc-950 dark:text-zinc-50">
                    SSLCommerz (saved card ending 4242)
                  </div>
                  <p className="mt-1 text-xs text-zinc-600 dark:text-zinc-300">
                    Charge today and renew automatically
                  </p>
                </div>
              </div>
            </OptionCard>
            <OptionCard selected={pay === 'new'} onSelect={() => setPay('new')}>
              <div className="font-medium text-zinc-950 dark:text-zinc-50">
                New payment method
              </div>
            </OptionCard>
            <OptionCard selected={pay === 'cash'} onSelect={() => setPay('cash')}>
              <div className="flex items-start gap-3">
                <HandCoins className="w-5 h-5 mt-0.5 text-zinc-500 dark:text-zinc-400 flex-shrink-0" />
                <div>
                  <div className="font-medium text-zinc-950 dark:text-zinc-50">
                    Cash to office
                  </div>
                  <p className="mt-1 text-xs text-zinc-600 dark:text-zinc-300">
                    Subscription updates after admin records the cash
                  </p>
                </div>
              </div>
            </OptionCard>
          </div>
        </Card>

        {/* Confirm + submit */}
        <Card className="p-5 sm:p-6">
          <label className="flex items-start gap-3 mb-5 cursor-pointer">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="mt-0.5 rounded border-zinc-300 dark:border-zinc-600 text-brand focus:ring-brand"
            />
            <span className="text-sm text-zinc-700 dark:text-zinc-200">
              I understand my plan changes immediately and the charge above applies.
            </span>
          </label>

          <div className="flex items-center justify-between gap-3 pt-5 border-t border-zinc-200 dark:border-zinc-700">
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate({ to: '/subscription' })}
              iconLeft={<ArrowLeft className="w-4 h-4" />}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={submitting}
              disabled={!confirmed || target === currentPkgId}
              iconRight={!submitting ? <ArrowRight className="w-4 h-4" /> : undefined}
            >
              {netToday > 0 ? (
                <>
                  Pay <Money minor={netToday} hideCents tone="neutral" className="ml-1 text-white dark:text-zinc-950" />
                </>
              ) : (
                'Confirm'
              )}
            </Button>
          </div>
        </Card>

        {payError && (
          <p className="text-xs text-rose-600 dark:text-rose-400 text-center flex items-center justify-center gap-1.5">
            <X className="w-3.5 h-3.5" /> {payError}
          </p>
        )}
        {confirmed && !payError && (
          <p className="text-xs text-emerald-700 dark:text-emerald-400 text-center flex items-center justify-center gap-1.5">
            <CheckCircle2 className="w-3.5 h-3.5" /> Ready to confirm
          </p>
        )}
      </form>
    </div>
  );
}
